/**
 * Mail feature API layer — OpenAPI-first (§9.1, §9.3).
 *
 * Mail logs query + send mail / send notification.
 */

import { useAdminMailLogsList as useGenList } from "@shared/api/generated/admin/admin";
import {
  mailSend as genMailSend,
  notificationsSend as genNotificationsSend,
  useMailSend as useGenMailSend,
  useNotificationsSend as useGenNotificationsSend,
} from "@shared/api/generated/mail/mail";
import type { AdminMailLogsListParams } from "@shared/api/generated/model";

// Backward-compatible aliases used by MailModal and other consumers
export type {
  MailAttachment,
  MailAttachment as MailAttachmentPayload,
  MailAttachmentInfo,
  MailLogEntry,
  MailSendRequest,
  MailSendRequest as MailSendPayload,
  NotificationSendRequest,
  NotificationSendRequest as NotificationSendPayload,
  NotificationSendResponse,
} from "@shared/api/generated/model";

/** Channel selector type used by MailModal (not generated — enum-like). */
export type NotificationChannel = "email" | "telegram";

// ── Queries ─────────────────────────────────────────────────────────────────

export const useMailLogs = (filters?: { status?: string; limit?: number }) => {
  const status = filters?.status?.trim() || undefined;
  const limit = filters?.limit ?? 100;
  const params: AdminMailLogsListParams = {
    ...(status && { status }),
    limit,
  };
  return useGenList(params, { query: { staleTime: 60_000 } });
};

// ── Send helpers (plain async functions — backward-compatible) ──────────────

export const sendMail = (payload: {
  to: string;
  subject: string;
  body: string;
  attachments?: { filename: string; content: string; content_type?: string }[];
}) => genMailSend(payload);

export const sendNotification = (payload: {
  user_id?: string;
  to?: string;
  telegram_id?: number | null;
  channels?: string[];
  subject?: string;
  body: string;
  attachments?: { filename: string; content: string; content_type?: string }[];
}) => genNotificationsSend(payload);

// ── Mutation hooks ──────────────────────────────────────────────────────────

export const useSendMail = () => useGenMailSend();
export const useSendNotification = () => useGenNotificationsSend();
